import {
    type ComponentProps,
    type ReactElement,
    type ReactNode,
    type Ref,
} from "react";
import classNames from "classnames";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@design-system/Popover/Popover";
import { isDisabledElement } from "@design-system/overlay/isDisabledElement";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import styles from "./SettingsPanel.module.scss";
import { hasSlotContent } from "./SettingsPanelParts";

type PopoverRootProps = ComponentProps<typeof Popover>;

export interface SettingsPanelRootProps {
    /** The element that opens the panel, usually an icon-only `Button`. */
    trigger: ReactElement;
    /** Hover hint on the trigger. */
    triggerTooltip?: string;
    /** Pinned below the scroll area, so it never scrolls; usually one `Action`. */
    footer?: ReactNode;
    /** The scroll area, for drag auto-scroll. */
    scrollContainerRef?: Ref<HTMLDivElement>;
    portalContainer?: HTMLElement;
    open?: PopoverRootProps["open"];
    defaultOpen?: PopoverRootProps["defaultOpen"];
    onOpenChange?: PopoverRootProps["onOpenChange"];
    /** Extra class on the popup surface. */
    className?: string;
    children: ReactNode;
}

/**
 * A settings popover with the Menu and Picker look: `trigger` opens a panel
 * of `Section`s, `Row`s and `ToggleRow`s that scrolls once it reaches the
 * height available, with an optional pinned `footer`. Unlike `Menu`, rows
 * can hold any control and the panel stays open while they are used.
 */
export const SettingsPanelRoot = ({
    trigger,
    triggerTooltip,
    footer,
    scrollContainerRef,
    portalContainer,
    open,
    defaultOpen,
    onOpenChange,
    className,
    children,
}: SettingsPanelRootProps) => {
    const popoverTrigger = (
        <PopoverTrigger disabled={isDisabledElement(trigger)}>
            {trigger}
        </PopoverTrigger>
    );

    return (
        <Popover
            open={open}
            defaultOpen={defaultOpen}
            onOpenChange={onOpenChange}
        >
            {triggerTooltip === undefined ? (
                popoverTrigger
            ) : (
                <Tooltip content={triggerTooltip}>{popoverTrigger}</Tooltip>
            )}
            <PopoverContent
                align="end"
                sideOffset={4}
                portalContainer={portalContainer}
                className={classNames(styles.popup, className)}
            >
                <div ref={scrollContainerRef} className={styles.scrollArea}>
                    {children}
                </div>
                {hasSlotContent(footer) ? (
                    <div className={styles.footer}>{footer}</div>
                ) : null}
            </PopoverContent>
        </Popover>
    );
};
